<script>
  // Live solver output.
  //
  // Was a disabled <textarea> at `width: 75%; height: 150px`, centred, with the
  // page's body font. Six lines visible at a time, of output that runs to
  // hundreds, and no way to copy it out (a disabled textarea is not
  // selectable in every browser).
  //
  // Auto-scroll is conditional, which is the part that matters: it follows the
  // tail only while the user is already AT the tail. Scrolling up to read
  // something detaches it, so the panel stops yanking the view away
  // mid-sentence, and a "Jump to latest" button re-attaches.
  import { tick } from "svelte";

  export let text = "";
  export let label = "Solver output";
  /** Any CSS length. Tall enough to read a stack of iterations at a glance. */
  export let height = "18rem";

  let panel;
  let following = true;
  let copyState = "idle";

  // Re-run whenever `text` changes -- that is the socket appending a chunk.
  $: text, followTail();

  async function followTail() {
    if (!following || !panel) return;
    await tick();
    panel.scrollTop = panel.scrollHeight;
  }

  // 24px of slack: "close enough to the bottom" survives sub-pixel rounding and
  // the moment between a chunk landing and the scroll being applied.
  function onScroll() {
    const distance = panel.scrollHeight - panel.scrollTop - panel.clientHeight;
    following = distance < 24;
  }

  async function jumpToLatest() {
    following = true;
    await followTail();
  }

  async function copy() {
    try {
      // navigator.clipboard needs a secure context. Production is https, but a
      // developer on http://<lan-ip>:3333 is not, so fall back rather than
      // throwing at them.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        legacyCopy(text);
      }
      copyState = "done";
    } catch {
      copyState = "failed";
    }
    setTimeout(() => (copyState = "idle"), 1600);
  }

  function legacyCopy(value) {
    const scratch = document.createElement("textarea");
    scratch.value = value;
    scratch.setAttribute("readonly", "");
    scratch.style.position = "fixed";
    scratch.style.opacity = "0";
    document.body.appendChild(scratch);
    scratch.select();
    document.execCommand("copy");
    document.body.removeChild(scratch);
  }
</script>

<div class="mt-4">
  <div class="mb-1.5 flex items-center justify-between gap-3">
    <span class="text-xs font-medium uppercase tracking-wide opacity-50">
      {label}
    </span>

    <div class="flex items-center gap-1">
      {#if !following}
        <button
          type="button"
          class="btn btn-ghost btn-xs gap-1.5 normal-case"
          on:click={jumpToLatest}
        >
          <i class="fas fa-arrow-down text-[0.65rem]" aria-hidden="true"></i>
          Jump to latest
        </button>
      {/if}
      <button
        type="button"
        class="btn btn-ghost btn-xs gap-1.5 normal-case"
        on:click={copy}
        disabled={!text}
      >
        <i
          class="fas {copyState === 'done'
            ? 'fa-check'
            : copyState === 'failed'
              ? 'fa-times'
              : 'fa-copy'} text-[0.65rem]"
          aria-hidden="true"
        ></i>
        {copyState === "done"
          ? "Copied"
          : copyState === "failed"
            ? "Copy failed"
            : "Copy"}
      </button>
    </div>
  </div>

  <!-- tabindex on a non-interactive element, deliberately: this is a scrollable
       region, and without it a keyboard user cannot scroll the solver output at
       all. WCAG 2.1.1 wants exactly this.
       svelte-ignore a11y-no-noninteractive-tabindex -->
  <div
    class="log-panel p-3"
    style="height: {height}"
    bind:this={panel}
    on:scroll={onScroll}
    tabindex="0"
    role="log"
    aria-live="polite"
    aria-label={label}
  >
    {#if text}{text}{:else}<span class="opacity-40">Waiting for output…</span
      >{/if}
  </div>
</div>
